import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import shellData from '../config/ui-shell.json';
import { FeaturesConfig, LayoutConfig, ThemeConfig } from '../types/config';

const LS_KEY = 'ui-shell';

export interface UiShellConfig {
  layout: LayoutConfig;
  theme: ThemeConfig;
  features: FeaturesConfig;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
}

function mergeUiShell(base: UiShellConfig, saved: Partial<UiShellConfig>): UiShellConfig {
  const savedSidebar = saved.layout?.sidebar;
  return {
    layout: {
      ...base.layout,
      ...saved.layout,
      header: { ...base.layout.header, ...saved.layout?.header },
      sidebar: {
        ...base.layout.sidebar,
        ...savedSidebar,
        enabled: asBoolean(savedSidebar?.enabled, base.layout.sidebar.enabled),
        collapsible: asBoolean(savedSidebar?.collapsible, base.layout.sidebar.collapsible),
        defaultCollapsed: asBoolean(savedSidebar?.defaultCollapsed, base.layout.sidebar.defaultCollapsed),
      },
    },
    theme: {
      ...base.theme,
      ...saved.theme,
      customColors: { ...base.theme.customColors, ...saved.theme?.customColors },
    },
    features: { ...base.features, ...saved.features },
  };
}

function readLocalShell(): UiShellConfig {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<UiShellConfig>;
      return mergeUiShell(shellData as UiShellConfig, parsed);
    }
  } catch (err) {
    console.error('Failed to load ui-shell from localStorage:', err);
  }
  return shellData as UiShellConfig;
}

const defaultShell = shellData as UiShellConfig;

type ConfigContextValue = {
  config: UiShellConfig;
  loading: boolean;
  updateConfig: (updates: Partial<UiShellConfig>) => void;
  resetConfig: () => void;
};

const ConfigContext = createContext<ConfigContextValue | null>(null);

export function ConfigProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<UiShellConfig>(defaultShell);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setConfig(readLocalShell());
    setLoading(false);
  }, []);

  const updateConfig = useCallback((updates: Partial<UiShellConfig>) => {
    setConfig((prev) => {
      const next = mergeUiShell(prev, updates);
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(next));
      } catch (err) {
        console.error('Failed to save ui-shell:', err);
      }
      return next;
    });
  }, []);

  const resetConfig = useCallback(() => {
    localStorage.removeItem(LS_KEY);
    setConfig(defaultShell);
  }, []);

  const value = useMemo(
    () => ({ config, loading, updateConfig, resetConfig }),
    [config, loading, updateConfig, resetConfig]
  );

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useConfigContext(): ConfigContextValue {
  const ctx = useContext(ConfigContext);
  if (!ctx) {
    throw new Error('useConfigContext must be used within ConfigProvider');
  }
  return ctx;
}
